import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const ShedWanted = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    shedType: '',
    sizeRange: '',
    budgetRange: '',
    useCase: '',
    rtoInterested: false,
    details: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Shed Wanted submission:', form);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Request Submitted | Find Your Perfect Shed</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 pt-24 pb-16 flex items-center justify-center">
          <div className="text-center px-4 max-w-md">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">&#10003;</div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Request submitted</h1>
            <p className="text-gray-500 mb-6">
              Dealers in your area will be notified. Expect to hear back within 1-2 business days.
            </p>
            <a href="/sheds" className="inline-block bg-gray-900 text-white text-sm font-medium px-6 py-3 hover:bg-gray-800 transition-colors">
              Browse Available Sheds
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Shed Wanted - Tell Us What You Need</title>
        <meta name="description" content="Post what kind of shed you're looking for and get matched with local dealers who have what you need. Free, fast and no obligation." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Tell us what you need</h1>
            <p className="text-gray-500">
              Describe the shed you're looking for and we'll match you with dealers in your area. No obligation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-6 space-y-6">
            {/* Contact Info */}
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Your contact info</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Full name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={form.city}
                      onChange={handleChange}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">State *</label>
                    <input
                      type="text"
                      name="state"
                      required
                      value={form.state}
                      onChange={handleChange}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                      maxLength={2}
                      placeholder="TX"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shed Details */}
            <div className="border-t border-gray-100 pt-6">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">What are you looking for?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Shed type</label>
                  <select
                    name="shedType"
                    value={form.shedType}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 bg-white"
                  >
                    <option value="">Any type</option>
                    <option value="barn">Barn</option>
                    <option value="gable">Gable</option>
                    <option value="workshop">Workshop</option>
                    <option value="lean-to">Lean-To</option>
                    <option value="ranch">Ranch</option>
                    <option value="studio">Studio / She-Shed</option>
                    <option value="cottage">Cottage</option>
                    <option value="garage">Garage</option>
                    <option value="utility">Utility</option>
                    <option value="cabana">Cabana / Pool House</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Size range</label>
                  <select
                    name="sizeRange"
                    value={form.sizeRange}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 bg-white"
                  >
                    <option value="">Any size</option>
                    <option value="small">Small (under 80 sq ft)</option>
                    <option value="medium">Medium (80-150 sq ft)</option>
                    <option value="large">Large (150-300 sq ft)</option>
                    <option value="xlarge">Extra Large (300+ sq ft)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Budget range</label>
                  <select
                    name="budgetRange"
                    value={form.budgetRange}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 bg-white"
                  >
                    <option value="">Any budget</option>
                    <option value="under-2500">Under $2,500</option>
                    <option value="2500-5000">$2,500 - $5,000</option>
                    <option value="5000-10000">$5,000 - $10,000</option>
                    <option value="10000-20000">$10,000 - $20,000</option>
                    <option value="over-20000">$20,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Primary use</label>
                  <select
                    name="useCase"
                    value={form.useCase}
                    onChange={handleChange}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 bg-white"
                  >
                    <option value="">Select use</option>
                    <option value="storage">General storage</option>
                    <option value="workshop">Workshop / Tools</option>
                    <option value="office">Home office</option>
                    <option value="she-shed">She-shed / Man-cave</option>
                    <option value="garage">Vehicle / Equipment</option>
                    <option value="garden">Garden shed</option>
                    <option value="pool-house">Pool house</option>
                    <option value="adu">ADU / Living space</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="border-t border-gray-100 pt-6">
              <label className="block text-sm text-gray-600 mb-1">Anything else we should know?</label>
              <textarea
                name="details"
                rows={3}
                value={form.details}
                onChange={handleChange}
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                placeholder="Color preferences, specific features needed, timeline, etc."
              />
            </div>

            {/* RTO Checkbox */}
            <div className="flex items-center gap-2 border-t border-gray-100 pt-6">
              <input
                type="checkbox"
                name="rtoInterested"
                id="rtoInterested"
                checked={form.rtoInterested}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <label htmlFor="rtoInterested" className="text-sm text-gray-600">
                I'm interested in Rent-to-Own financing options
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-gray-900 text-white text-sm font-medium py-3.5 hover:bg-gray-800 transition-colors"
            >
              Submit Request
            </button>

            <p className="text-xs text-gray-400 text-center">
              Your info is shared only with dealers in your area. No spam. No obligation.
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

export default ShedWanted;
